/**
 * Skeleton Loading Components
 * ===========================
 *
 * 2025 Best Practices Implementation:
 * - Smooth shimmer animation for perceived performance
 * - Various preset shapes (card, text, avatar, chart)
 * - Composable for complex layouts
 * - Reduced layout shift (CLS optimization)
 * - Dark mode compatible
 *
 * @author ASTRA Dashboard Enhancement
 * @date December 2025
 */

import React from 'react';
import { motion } from 'framer-motion';

/**
 * Base Skeleton Component with shimmer animation
 */
export const Skeleton = ({
  className = '',
  width,
  height,
  rounded = 'md',
  animate = true,
  children,
}) => {
  const roundedClasses = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    full: 'rounded-full',
  };

  const style = {
    width: width || '100%',
    height: height || '1rem',
  };

  return (
    <div
      className={`
        relative overflow-hidden
        bg-gradient-to-r from-muted via-muted/80 to-muted
        ${roundedClasses[rounded] || roundedClasses.md}
        ${animate ? 'skeleton-shimmer' : ''}
        ${className}
      `}
      style={style}
    >
      {/* Shimmer overlay */}
      {animate && (
        <div
          className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/10 to-transparent"
        />
      )}
      {children}
    </div>
  );
};

/**
 * Text Line Skeleton
 */
export const SkeletonText = ({
  lines = 1,
  className = '',
  lastLineWidth = '60%',
}) => {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          height="0.875rem"
          width={i === lines - 1 && lines > 1 ? lastLineWidth : '100%'}
          rounded="sm"
        />
      ))}
    </div>
  );
};

/**
 * Avatar/Circle Skeleton
 */
export const SkeletonAvatar = ({
  size = 40,
  className = '',
}) => {
  return (
    <Skeleton
      width={size}
      height={size}
      rounded="full"
      className={className}
    />
  );
};

/**
 * Card Skeleton
 */
export const SkeletonCard = ({
  className = '',
  showHeader = true,
  showContent = true,
  showFooter = false,
  contentLines = 3,
}) => {
  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      {showHeader && (
        <div className="flex items-center gap-3 mb-4">
          <SkeletonAvatar size={32} />
          <div className="flex-1 space-y-1.5">
            <Skeleton height="0.75rem" width="40%" />
            <Skeleton height="0.625rem" width="25%" />
          </div>
        </div>
      )}

      {showContent && (
        <SkeletonText lines={contentLines} className="mb-4" />
      )}

      {showFooter && (
        <div className="flex gap-2 pt-4 border-t border-border">
          <Skeleton height="2rem" width="5rem" rounded="md" />
          <Skeleton height="2rem" width="5rem" rounded="md" />
        </div>
      )}
    </div>
  );
};

/**
 * Metric Card Skeleton (for dashboard stats)
 */
export const SkeletonMetricCard = ({ className = '' }) => {
  return (
    <div className={`bg-card border border-border rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <Skeleton height="0.75rem" width="50%" rounded="sm" />
        <SkeletonAvatar size={24} />
      </div>
      <Skeleton height="2rem" width="70%" className="mb-2" />
      <div className="flex items-center gap-2">
        <Skeleton height="0.75rem" width="3rem" rounded="full" />
        <Skeleton height="0.625rem" width="4rem" />
      </div>
    </div>
  );
};

/**
 * Chart Skeleton
 */
export const SkeletonChart = ({
  type = 'line', // 'line', 'bar', 'pie', 'candlestick'
  height = 200,
  className = '',
}) => {
  const renderChartBars = () => {
    const barCount = type === 'candlestick' ? 20 : 12;
    return (
      <div className="flex items-end justify-between h-full px-4 pb-4 gap-1">
        {Array.from({ length: barCount }).map((_, i) => (
          <div
            key={i}
            className="flex-1 bg-muted rounded-t animate-pulse"
            style={{
              height: `${Math.random() * 60 + 20}%`,
              animationDelay: `${i * 50}ms`,
            }}
          />
        ))}
      </div>
    );
  };

  const renderLinePath = () => (
    <svg className="w-full h-full" viewBox="0 0 400 200" preserveAspectRatio="none">
      <defs>
        <linearGradient id="shimmer" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgb(var(--muted))" />
          <stop offset="50%" stopColor="rgb(var(--muted) / 0.5)" />
          <stop offset="100%" stopColor="rgb(var(--muted))" />
          <animate
            attributeName="x1"
            values="-100%;100%"
            dur="1.5s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="x2"
            values="0%;200%"
            dur="1.5s"
            repeatCount="indefinite"
          />
        </linearGradient>
      </defs>
      <path
        d="M0,150 Q50,120 100,130 T200,100 T300,120 T400,80"
        fill="none"
        stroke="url(#shimmer)"
        strokeWidth="3"
        className="opacity-50"
      />
      <path
        d="M0,150 Q50,120 100,130 T200,100 T300,120 T400,80 V200 H0 Z"
        fill="url(#shimmer)"
        className="opacity-10"
      />
    </svg>
  );

  const renderPie = () => (
    <div className="flex items-center justify-center h-full">
      <Skeleton width={height * 0.7} height={height * 0.7} rounded="full" />
    </div>
  );

  return (
    <div
      className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}
      style={{ height }}
    >
      {type === 'pie' ? renderPie() : type === 'line' ? renderLinePath() : renderChartBars()}
    </div>
  );
};

/**
 * Table Row Skeleton
 */
export const SkeletonTableRow = ({
  columns = 5,
  className = '',
}) => {
  return (
    <tr className={className}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton height="0.875rem" width={i === 0 ? '60%' : '80%'} />
        </td>
      ))}
    </tr>
  );
};

/**
 * Table Skeleton
 */
export const SkeletonTable = ({
  rows = 5,
  columns = 5,
  showHeader = true,
  className = '',
}) => {
  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      <table className="w-full">
        {showHeader && (
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              {Array.from({ length: columns }).map((_, i) => (
                <th key={i} className="px-4 py-3 text-left">
                  <Skeleton height="0.75rem" width="60%" />
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className="divide-y divide-border">
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Dashboard Grid Skeleton
 */
export const SkeletonDashboard = ({ className = '' }) => {
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonMetricCard key={i} />
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonChart type="line" height={300} />
        <SkeletonChart type="bar" height={300} />
      </div>

      {/* Table */}
      <SkeletonTable rows={5} columns={6} />
    </div>
  );
};

/**
 * List Item Skeleton
 */
export const SkeletonListItem = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-3 p-3 ${className}`}>
      <SkeletonAvatar size={36} />
      <div className="flex-1 space-y-1.5">
        <Skeleton height="0.875rem" width="70%" />
        <Skeleton height="0.625rem" width="40%" />
      </div>
      <Skeleton height="1.5rem" width="3rem" rounded="md" />
    </div>
  );
};

/**
 * List Skeleton
 */
export const SkeletonList = ({
  items = 5,
  showDivider = true,
  className = '',
}) => {
  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {Array.from({ length: items }).map((_, i) => (
        <div
          key={i}
          className={showDivider && i < items - 1 ? 'border-b border-border' : ''}
        >
          <SkeletonListItem />
        </div>
      ))}
    </div>
  );
};

/**
 * Portfolio Card Skeleton
 */
export const SkeletonPortfolioCard = ({ className = '' }) => {
  return (
    <div className={`bg-card border border-border rounded-xl p-6 ${className}`}>
      <div className="flex items-start justify-between mb-6">
        <div className="space-y-2">
          <Skeleton height="0.75rem" width="6rem" />
          <Skeleton height="2.5rem" width="12rem" />
        </div>
        <Skeleton height="2rem" width="4rem" rounded="full" />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-1">
            <Skeleton height="0.625rem" width="60%" />
            <Skeleton height="1.25rem" width="80%" />
          </div>
        ))}
      </div>

      <SkeletonChart type="line" height={120} />
    </div>
  );
};

/**
 * Trading Card Skeleton
 */
export const SkeletonTradingCard = ({ className = '' }) => {
  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`}>
      <div className="flex items-center gap-3 mb-3">
        <SkeletonAvatar size={28} />
        <div className="flex-1">
          <Skeleton height="0.875rem" width="40%" className="mb-1" />
          <Skeleton height="0.625rem" width="25%" />
        </div>
        <Skeleton height="1.5rem" width="4rem" rounded="md" />
      </div>
      <div className="flex justify-between items-center">
        <Skeleton height="1.25rem" width="30%" />
        <Skeleton height="0.75rem" width="20%" />
      </div>
    </div>
  );
};

// Add shimmer animation to CSS (should be in global CSS)
export const skeletonStyles = `
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .animate-shimmer {
    animation: shimmer 1.5s infinite;
  }

  .skeleton-shimmer {
    position: relative;
    overflow: hidden;
  }

  .skeleton-shimmer::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.08) 50%,
      transparent 100%
    );
    animation: shimmer 1.5s infinite;
  }
`;

export default Skeleton;
